import "./style.scss";

import KeyboardArrowDownSharpIcon from "@mui/icons-material/KeyboardArrowDownSharp";
import AlertTriangle from "assets/img/svg/alertTriangle";
import AdvanceCustomStatusField from "components/AdvanceManagement/AdvanceCustomStatusField";
import { validateAdvanceSlicePaymentDate } from "components/AdvanceManagement/utils/advanceSliceAmounts";
import {
    advanceSliceDisabledTooltipKey,
    advanceSlicePeriodWarningKey,
    advanceSliceRowKey,
    getAdvanceSliceLock,
    getSlicePaymentStatusStyles,
    paymentStatusForNewSliceDate,
} from "components/AdvanceManagement/utils/advanceSliceFieldRules";
import type { BasicTableAfterRow, BasicTableColumn } from "components/shared/BasicTable";
import BasicTable from "components/shared/BasicTable";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import LightPlusBtn from "components/shared/LightPlus";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { isBookPublisher } from "helpers/publisher";
import { getCurrencySymbol } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { FieldArrayWithId } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import { PaymentStatus } from "store/types";
import type { Binary } from "types";

import variables from "../../../assets/styles/_variables.module.scss";
import type { AdvanceSlicesFormData } from "../AdvanceSlicesFormBody";
import type { TAdvancePayment } from "../NewAdvanceModal";

type SliceField = FieldArrayWithId<TAdvancePayment, "slices", "id">;

const paymentStatusMenuItemSx = {
    "&.MuiMenuItem-root": {
        margin: "4px 6px",
        minHeight: "24px",
        padding: "2px 8px",
        fontSize: "13px",
        lineHeight: "18px",
    },
};

type AdvanceSlicesTableProps = {
    formData: AdvanceSlicesFormData;
    fields: SliceField[];
    watchedSlices: TAdvancePayment["slices"];
    removeRow: (index: number) => void;
    isUpdate: boolean;
    isForfait: Binary;
    readOnly: boolean;
    minPaymentDate?: Date;
};

function sliceDisabledTooltip(
    t: (key: string) => string,
    slice: SliceField,
    isUpdate: boolean,
    readOnly: boolean,
    isPeriodClosed: boolean,
) {
    const lock = getAdvanceSliceLock(slice, isUpdate, readOnly, isPeriodClosed);
    const tooltipKey = advanceSliceDisabledTooltipKey({ readOnly, isPeriodClosed, isUpdate, ...lock });
    return { ...lock, disabledTooltipText: tooltipKey ? t(tooltipKey) : "" };
}

const AdvanceSliceDateCell = ({
    slice,
    index,
    formData,
    watchedSlices,
    isUpdate,
    readOnly,
    minPaymentDate,
}: {
    slice: SliceField;
    index: number;
    formData: AdvanceSlicesFormData;
    watchedSlices: TAdvancePayment["slices"];
    isUpdate: boolean;
    readOnly: boolean;
    minPaymentDate?: Date;
}) => {
    const [t] = useTranslation();
    const { control, setValue } = formData;
    const isPeriodClosed = isUpdate && Boolean(slice.periodClosed);
    const { isDisabled, disabledTooltipText } = sliceDisabledTooltip(t, slice, isUpdate, readOnly, isPeriodClosed);
    const paidStatus = watchedSlices[index]?.status ?? PaymentStatus.NOT_PAID;

    const handleChangeDate = (event: { name: string; value: Date }) => {
        if (slice.sliceId) return;
        const rowIndex = +event.name.split(".")[1];
        setValue(`slices.${rowIndex}.status`, paymentStatusForNewSliceDate(event.value));
    };

    return (
        <div className="advance-slices-date-cell">
            <Controller
                name={`slices.${index}.paymentDate`}
                control={control}
                rules={{
                    onChange: (event) => {
                        handleChangeDate(event.target);
                    },
                    validate: (value) => {
                        if (paidStatus !== PaymentStatus.NOT_PAID && !value) return t("AdvancePayment.Required");
                        return validateAdvanceSlicePaymentDate(value, minPaymentDate, {
                            invalidDate: t("AdvancePayment.InvalidDate"),
                            beforePeriodStart: t("AdvancePayment.PaymentDateBeforePeriodStart"),
                        });
                    },
                }}
                render={({ field, formState: { errors } }) => {
                    const dateError = errors.slices?.[index]?.paymentDate;
                    const hasDateError = dateError?.type === "required" || dateError?.type === "validate";
                    return (
                        <StyledDatePicker
                            {...field}
                            inputFormat="dd/MM/yy"
                            disabled={isDisabled}
                            disabledText={disabledTooltipText}
                            maxYearDistance={2}
                            minDate={minPaymentDate}
                            error={hasDateError}
                            inputSize="small"
                            textFieldSx={{
                                width: "100%",
                                minWidth: 0,
                                ".MuiSvgIcon-root": {
                                    fontSize: "14px",
                                },
                            }}
                        />
                    );
                }}
            />
        </div>
    );
};

function fieldErrorMessage(message: unknown) {
    return typeof message === "string" && message ? message : null;
}

function isBlankAmount(amount: string | number | null | undefined) {
    return amount == null || String(amount).trim() === "";
}

/**
 * Editable advance slices, laid out like the title cumulative-sales table.
 */
const AdvanceSlicesTable = ({
    formData,
    fields,
    watchedSlices,
    removeRow,
    isUpdate,
    isForfait,
    readOnly,
    minPaymentDate,
}: AdvanceSlicesTableProps) => {
    const [t] = useTranslation();
    const { control, errors, trigger } = formData;
    const { periods, activePeriodData } = useAppSelector((state) => state.period);
    const { companyInfos } = useCompanySettings();
    const showCustomStatus = isBookPublisher(companyInfos?.publisherType);
    const currencySymbol = getCurrencySymbol(companyInfos?.currency ?? "EUR");
    const showDelete = !readOnly && fields.length > 1;

    const columns: BasicTableColumn<SliceField>[] = [
        {
            header: "n°",
            align: "center",
            headerClassName: "advance-slices-index-cell",
            cellClassName: "advance-slices-index-cell",
            render: (_slice, index) => <span>{index + 1}</span>,
        },
        {
            header: `${t("AdvancePayment.Amount")} ${currencySymbol}`,
            align: "right",
            headerClassName: "advance-slices-amount-column",
            cellClassName: "advance-slices-value-cell advance-slices-amount-cell advance-slices-amount-column",
            render: (slice, index) => {
                const isPeriodClosed = isUpdate && Boolean(slice.periodClosed);
                const { isDisabled, disabledTooltipText } = sliceDisabledTooltip(
                    t,
                    slice,
                    isUpdate,
                    readOnly,
                    isPeriodClosed,
                );
                const amountMissing = isBlankAmount(watchedSlices[index]?.amount);
                const colorEmptyAmount = Boolean(errors.slices?.[index]?.amount) && amountMissing;
                return (
                    <>
                        {colorEmptyAmount && <span className="advance-slices-amount-fill" aria-hidden />}
                        <div className="advance-slices-amount-input">
                            <NumberFormComponent
                                label=""
                                control={control}
                                placeholder="0"
                                controlName={`slices.${index}.amount`}
                                error={errors.slices?.[index]?.amount}
                                float
                                required
                                disabled={isDisabled}
                                disabledText={disabledTooltipText}
                                hideDisabledLockIcon
                            />
                        </div>
                    </>
                );
            },
        },
        {
            header: t("AdvancePayment.PaymentDate"),
            headerClassName: "advance-slices-date-column",
            cellClassName: "advance-slices-value-cell advance-slices-date-column",
            render: (slice, index) => (
                <AdvanceSliceDateCell
                    slice={slice}
                    index={index}
                    formData={formData}
                    watchedSlices={watchedSlices}
                    isUpdate={isUpdate}
                    readOnly={readOnly}
                    minPaymentDate={minPaymentDate}
                />
            ),
        },
        {
            header: t("AdvancePayment.Comment"),
            cellClassName: "advance-slices-value-cell advance-slices-comment-cell",
            render: (slice, index) => {
                const isPeriodClosed = isUpdate && Boolean(slice.periodClosed);
                const { isDisabled, disabledTooltipText } = sliceDisabledTooltip(
                    t,
                    slice,
                    isUpdate,
                    readOnly,
                    isPeriodClosed,
                );
                return (
                    <Controller
                        name={`slices.${index}.comment`}
                        control={control}
                        render={({ field }) => (
                            <StyledTextField
                                {...field}
                                placeholder={t("AdvancePayment.Comment")}
                                name="comment"
                                fullWidth
                                disabled={isDisabled}
                                disabledText={disabledTooltipText}
                            />
                        )}
                    />
                );
            },
        },
        ...(showCustomStatus
            ? [
                  {
                      header: t("AdvancePayment.CustomStatus"),
                      headerClassName: "advance-slices-custom-status-cell",
                      cellClassName: "advance-slices-value-cell advance-slices-custom-status-cell",
                      render: (slice: SliceField, index: number) => (
                          <AdvanceCustomStatusField
                              control={control}
                              controlName={`slices.${index}.customStatusId`}
                              disabled={readOnly || (isUpdate && Boolean(slice.periodClosed))}
                              sx={{
                                  width: "100%",
                                  minWidth: 0,
                                  height: "24px",
                                  fontSize: "13px",
                                  "&.MuiOutlinedInput-root .MuiSelect-select": {
                                      minHeight: "unset",
                                      padding: "2px 22px 2px 6px",
                                      fontSize: "13px",
                                  },
                                  ".MuiSvgIcon-root": { fontSize: "16px" },
                              }}
                          />
                      ),
                  } satisfies BasicTableColumn<SliceField>,
              ]
            : []),
        {
            header: t("AdvancePayment.Status"),
            headerClassName: "advance-slices-status-cell",
            cellClassName: "advance-slices-status-cell",
            render: (slice, index) => {
                const isPeriodClosed = isUpdate && Boolean(slice.periodClosed);
                const { isDisabled, disabledTooltipText } = sliceDisabledTooltip(
                    t,
                    slice,
                    isUpdate,
                    readOnly,
                    isPeriodClosed,
                );
                const paidStatus = watchedSlices[index]?.status ?? PaymentStatus.NOT_PAID;
                const statusStyles = getSlicePaymentStatusStyles(paidStatus);
                return (
                    <Controller
                        control={control}
                        name={`slices.${index}.status`}
                        render={({ field }) => (
                            <StyledSelect
                                {...field}
                                onChange={(event) => {
                                    field.onChange(event);
                                    void trigger(`slices.${index}.paymentDate`);
                                }}
                                IconComponent={KeyboardArrowDownSharpIcon}
                                buttonType
                                noGradient
                                disabled={slice.sliceId ? true : isDisabled}
                                tooltipText={disabledTooltipText}
                                sx={{
                                    "&.MuiOutlinedInput-root": {
                                        fontSize: "13px",
                                        ".MuiOutlinedInput-input": {
                                            padding: "2px 22px 2px 8px",
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                        },
                                        ".MuiSvgIcon-root": {
                                            color: statusStyles.color,
                                            fontSize: "16px",
                                        },
                                        "& > fieldset": {
                                            border: "none",
                                        },
                                    },
                                    width: "100%",
                                    minWidth: "96px",
                                    height: "24px",
                                    color: statusStyles.color,
                                    fontFamily: "Poppins",
                                    backgroundColor: statusStyles.backgroundColor,
                                }}
                            >
                                <StyledMenuItem
                                    value={PaymentStatus.PAID}
                                    buttonColor="green"
                                    sx={paymentStatusMenuItemSx}
                                >
                                    {t("AdvancePayment.Paid")}
                                </StyledMenuItem>
                                <StyledMenuItem
                                    value={PaymentStatus.TO_PAY}
                                    buttonColor="orange"
                                    sx={paymentStatusMenuItemSx}
                                >
                                    {t("Filters.ForTransfer")}
                                </StyledMenuItem>
                                <StyledMenuItem
                                    value={PaymentStatus.NOT_PAID}
                                    buttonColor="red"
                                    sx={paymentStatusMenuItemSx}
                                >
                                    {t("AdvancePayment.NotPaid")}
                                </StyledMenuItem>
                            </StyledSelect>
                        )}
                    />
                );
            },
        },
        ...(showDelete
            ? [
                  {
                      header: "",
                      align: "center" as const,
                      headerClassName: "advance-slices-action-cell",
                      cellClassName: "advance-slices-action-cell",
                      render: (_slice: SliceField, index: number) => (
                          <LightPlusBtn
                              type="minus"
                              variant="black"
                              tooltipTitle={t("Beneficiars.Delete")}
                              onClick={() => removeRow(index)}
                          />
                      ),
                  } satisfies BasicTableColumn<SliceField>,
              ]
            : []),
    ];

    const renderAfterRow = (slice: SliceField, index: number) => {
        const isPeriodClosed = isUpdate && Boolean(slice.periodClosed);
        const { isDisabled } = getAdvanceSliceLock(slice, isUpdate, readOnly, isPeriodClosed);
        const warningKey = advanceSlicePeriodWarningKey({
            isDisabled,
            isForfait,
            paymentDate: watchedSlices[index]?.paymentDate,
            periods,
            activePeriod: activePeriodData,
        });
        const requiredMessages = new Set([t("NewElement.Required"), t("AdvancePayment.Required")]);
        const notices: { kind: "error" | "warning"; text: string }[] = [];
        let requiredShown = false;
        for (const text of [
            fieldErrorMessage(errors.slices?.[index]?.amount?.message),
            fieldErrorMessage(errors.slices?.[index]?.paymentDate?.message),
        ]) {
            if (!text) continue;
            if (requiredMessages.has(text)) {
                if (requiredShown) continue;
                requiredShown = true;
            }
            notices.push({ kind: "error", text });
        }
        if (warningKey) notices.push({ kind: "warning", text: t(warningKey) });
        if (!notices.length) return null;

        return notices.map(
            (notice): BasicTableAfterRow => ({
                leading: (
                    <AlertTriangle
                        width={13}
                        height={13}
                        color={notice.kind === "error" ? "#9B5067" : variables["orange-opaque-text"]}
                    />
                ),
                content: <p className={`advance-slices-notices__${notice.kind}`}>{notice.text}</p>,
            }),
        );
    };

    return (
        <div className="advance-slices-table">
            <BasicTable
                columns={columns}
                rows={fields}
                getRowKey={advanceSliceRowKey}
                renderAfterRow={renderAfterRow}
            />
        </div>
    );
};

export default AdvanceSlicesTable;
